import React from 'react';

import firebase from './firebase';
import { StateManager } from './stateManager';

/**
 * Sales credit — who gets paid for a piece of work.
 *
 * The same question ("which salesman earns this?") is asked about two different
 * things, so both store the same shape rather than each inventing its own:
 *
 *   - a service order   → `orders.sales_credits`
 *   - an NTO reduction  → `cars.nto_history[i].credits`
 *
 * A credit list is an array of `{ rep_id, rep_name, share }`, where `share` is an
 * integer percent and the shares sum to 100. One rep is `[{ share: 100 }]`; work two
 * reps did together splits between them. An empty array is a deliberate "nobody is
 * credited", which is different from the field being absent — absent means nobody has
 * decided yet. Either way, only a recorded credit pays.
 *
 * Credit is its own decision, kept apart from the reps already recorded on the car. The
 * rep who owns the car (`consign_rep`) is only ever a DEFAULT, on the NTO side, for
 * entries written before credit existed as a field. A service order takes no default at
 * all: whoever actually sold the work is credited, or nobody is.
 *
 * `share` is a percent and not a dollar figure on purpose. Amounts move — an SO's
 * revenue changes as services are added, and an NTO reduction is capped by what is
 * documented — so a stored dollar split would silently go stale. A percent stays
 * correct whatever the amount turns out to be.
 */

// Set when a human chose the credit, so a default can be told apart from a decision.
export const CREDIT_SOURCE = { MANUAL: 'manual', INFERRED: 'inferred' };

const clampShare = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
};

/** Names are matched the way rep pay already matches them: trimmed, case-insensitive. */
export const sameRepName = (a, b) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * Coerce whatever is on the document into a usable credit list. Rows without a rep,
 * and rows with no share, are dropped — they cannot be paid, and keeping them would
 * make the sum look wrong in the editor.
 */
export function normalizeCredits(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map(row => ({
      rep_id: (row && row.rep_id) || null,
      rep_name: (row && row.rep_name) ? String(row.rep_name).trim() : '',
      share: clampShare(row && row.share),
    }))
    .filter(row => (row.rep_id || row.rep_name) && row.share > 0);
}

export const totalShare = (credits) =>
  normalizeCredits(credits).reduce((sum, row) => sum + row.share, 0);

/** A list is saveable when it is empty ("no credit") or the shares add up to 100. */
export const creditsAreValid = (credits) => {
  const total = totalShare(credits);
  return total === 0 || total === 100;
};

/** One rep takes all of it. */
export const singleCredit = (rep) =>
  (rep && (rep.id || rep.name))
    ? [{ rep_id: rep.id || null, rep_name: rep.name || '', share: 100 }]
    : [];

/**
 * This rep's fraction of the work, 0–1.
 *
 * Matches on id first and falls back to name, because pay is calculated per contractor
 * and a contractor may have no Firestore user id configured — the same two-step match
 * `sales_calculations` already uses for deals.
 */
export function shareFor(credits, rep = {}) {
  const { id = null, name = '' } = rep;
  const match = normalizeCredits(credits).find(row =>
    (id && row.rep_id && row.rep_id === id) ||
    (!(id && row.rep_id) && sameRepName(row.rep_name, name))
  );
  return match ? match.share / 100 : 0;
}

/**
 * Even shares, with the remainder on the first row so the total is exactly 100.
 * Three reps split 34/33/33, never 33/33/33.
 */
export function splitEvenly(credits) {
  const rows = Array.isArray(credits) ? credits : [];
  if (!rows.length) return [];
  const base = Math.floor(100 / rows.length);
  const remainder = 100 - base * rows.length;
  return rows.map((row, i) => ({ ...row, share: base + (i === 0 ? remainder : 0) }));
}

/** "Ryan Tanski" for a sole credit, "Ryan Tanski 60% · Shane Hale 40%" for a split. */
export function creditsLabel(credits) {
  const rows = normalizeCredits(credits);
  if (!rows.length) return '';
  if (rows.length === 1) return rows[0].rep_name || 'Unnamed rep';
  return rows.map(row => `${row.rep_name || 'Unnamed rep'} ${row.share}%`).join(' · ');
}

/** First candidate that actually names somebody. Used to seed a default. */
export function firstCredit(...candidates) {
  for (const candidate of candidates) {
    const rows = normalizeCredits(candidate);
    if (rows.length) return rows;
  }
  return [];
}

/**
 * Credit for a service order. Only what is recorded on the order counts — an order with
 * no `sales_credits` is uncredited, not credited to somebody by inference.
 *
 * The rep who SOLD the car (`deals.sales_rep`) is deliberately NOT a fallback. Selling a
 * car and selling service work on it are two different jobs, often done by two different
 * people, and pay has always required a recorded credit. Borrowing the selling rep's name
 * made the order read as credited to them when nothing was owed to anybody. Surfaces that
 * want to show who sold the car read `deals.sales_rep` and label it as such.
 *
 * `orders.writer` is not a fallback either. The service writer is who wrote the order up
 * — usually the service manager, who is not on the sales team at all. Reports that want
 * the old writer grouping fall back to it themselves.
 */
export function resolveOrderCredits(order = {}) {
  return normalizeCredits(order.sales_credits);
}

/**
 * Credit for one NTO history entry. Entries written before this field existed fall back
 * to the car's consignment rep, which is exactly who the old calculation paid — so the
 * fallback reproduces the previous behaviour rather than dropping the credit.
 */
export function resolveNtoEntryCredits(entry = {}, car = {}) {
  if (Array.isArray(entry.credits)) return normalizeCredits(entry.credits);
  if (car.consign_rep) return singleCredit({ name: car.consign_rep });
  return [];
}

// The sales team is a small, rarely-changing list read by several dialogs. Cache the
// in-flight promise so opening three of them does not run three identical queries.
let salesTeamPromise = null;

export function fetchSalesTeam() {
  if (!salesTeamPromise) {
    salesTeamPromise = firebase.firestore().collection('users').where('salesRep', '==', true).get()
      .then(snap => snap.docs
        .map(d => ({ id: d.id, name: [d.data().firstName, d.data().lastName].filter(Boolean).join(' ') }))
        .filter(t => t.name)
        .sort((a, b) => a.name.localeCompare(b.name)))
      .catch(err => {
        console.error('Could not load the sales team', err);
        salesTeamPromise = null;
        return [];
      });
  }
  return salesTeamPromise;
}

export function useSalesTeam() {
  const [team, setTeam] = React.useState([]);
  React.useEffect(() => {
    let active = true;
    fetchSalesTeam().then(list => { if (active && list.length) setTeam(list); });
    return () => { active = false; };
  }, []);
  return team;
}

/** The signed-in user as a credit row, when they are on the sales team. */
export function currentUserCredit(salesTeam = []) {
  const byId = salesTeam.find(t => t.id === StateManager.userID);
  if (byId) return singleCredit(byId);
  const byName = salesTeam.find(t => sameRepName(t.name, StateManager.userName));
  return byName ? singleCredit(byName) : [];
}
